import { useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'

import Icon from '../icon/Icon'
import { ACHIEVEMENT_CATALOG } from '../../_defaults/achievement-catalog'
import { useAchievements } from '../../_hooks/use-achievements'
import {
  Anchor,
  ToastCard,
  Glyph,
  Body,
  Eyebrow,
  Title,
  Blurb,
  DismissButton,
} from './styled'

const AUTO_DISMISS_MS = 6000

/**
 * Pop-up toast that announces a freshly unlocked achievement. Reads from
 * `useAchievements()` and auto-dismisses after a few seconds. The user
 * can also dismiss via the × button.
 *
 * @returns {JSX.Element | null}
 */
const AchievementToast = (): JSX.Element | null => {
  const { recentlyUnlocked, dismissRecentlyUnlocked } = useAchievements()

  useEffect(() => {
    if (recentlyUnlocked === null) {
      return
    }

    const handle = setTimeout(() => {
      dismissRecentlyUnlocked()
    }, AUTO_DISMISS_MS)

    return () => {
      clearTimeout(handle)
    }
  }, [recentlyUnlocked, dismissRecentlyUnlocked])

  return (
    <Anchor>
      <AnimatePresence>
        {recentlyUnlocked !== null ? (
          <ToastCard
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.32 },
            }}
            exit={{
              opacity: 0,
              y: 24,
              scale: 0.96,
              transition: { duration: 0.32 },
            }}
          >
            <Glyph aria-hidden="true">
              <Icon icon={ACHIEVEMENT_CATALOG[recentlyUnlocked].glyph} />
            </Glyph>
            <Body>
              <Eyebrow>Achievement unlocked</Eyebrow>
              <Title>{ACHIEVEMENT_CATALOG[recentlyUnlocked].title}</Title>
              <Blurb>{ACHIEVEMENT_CATALOG[recentlyUnlocked].unlockBlurb}</Blurb>
            </Body>
            <DismissButton
              type="button"
              onClick={dismissRecentlyUnlocked}
              aria-label="Dismiss notification"
            >
              ×
            </DismissButton>
          </ToastCard>
        ) : null}
      </AnimatePresence>
    </Anchor>
  )
}

export default AchievementToast
